/**
 * Team → Daily update, Created today, Appraisal record and Workload; for an ops member the same reads back
 * "Done today" and "My record" (guide 5.5, 6.9; ADR-001 §7.6, §8.9; prototype VIEWS.daily, newtoday, record,
 * workload). All four are computed on read from the tables the ADR names: `activity_log` (what people did),
 * `tasks` / `proposed_items` / `dropped_items` (what was made today), `incidents` (the record) and open tasks
 * (workload). Nothing here is a score: the record is a list of facts (guide 6.9). No revenue in any payload.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid, Version } from './common.js';
import { ClientRef } from './clients.js';
import { IncidentCause, IncidentKind } from './history-import.js';
import { Severity } from './requests.js';
import { DropReason, TaskSource } from './tasks.js';
import { UserRef } from './users.js';
// ---------------------------------------------------------------------------
// GET /team/daily — Daily update (managers) / Done today (a member, themselves only)
// ---------------------------------------------------------------------------
export const TeamDailyQuery = z.object({
    /** The working day to read; default today (IST). Never in the future, at most 90 days back. */
    date: IsoDate.optional(),
    /** Managers: one person (an active ops member or the associate director). Members: only their own id. */
    user_id: Uuid.optional(),
});
/**
 * What counts as done (prototype DONEK): a task closed, a routine (a checklist or recurring task) closed, a meeting
 * logged, a client task kept, an escalation closed, a client request answered.
 */
export const DailyDoneKind = z.enum(['closed', 'routine', 'meeting', 'client_task', 'esc', 'request']).meta({ id: 'DailyDoneKind' });
/** What moved: a date pushed, a task left out, tasks created. */
export const DailyOtherKind = z.enum(['pushed', 'dropped', 'created']).meta({ id: 'DailyOtherKind' });
export const DailyDone = z
    .object({
    /** The activity_log row id (a bigint, as text). */
    id: z.string(),
    at: Timestamp,
    kind: DailyDoneKind,
    text: z.string(),
    /** null = internal work. Group the feed by this (prototype duGroup). */
    client: ClientRef.nullable(),
    task_id: Uuid.nullable(),
    /** The task's current closure carries proof (always true for a closed task; false for a meeting, etc.). */
    has_proof: z.boolean(),
})
    .meta({ id: 'DailyDone' });
export const DailyOther = z
    .object({ id: z.string(), at: Timestamp, kind: DailyOtherKind, text: z.string(), client: ClientRef.nullable(), task_id: Uuid.nullable() })
    .meta({ id: 'DailyOther' });
export const DailyOverdue = z
    .object({ task_id: Uuid, title: z.string(), client: ClientRef.nullable(), due: IsoDate, days_overdue: z.number().int().min(1) })
    .meta({ id: 'DailyOverdue' });
export const DailyPerson = z
    .object({
    user: UserRef,
    /** Oldest first, as the day went. */
    done: z.array(DailyDone),
    other: z.array(DailyOther),
    /** Still open and past due (as of now, not as of `date`), most overdue first. */
    overdue: z.array(DailyOverdue),
    /** Open tasks due on the next working day after `date`. */
    due_next_day: z.number().int().min(0),
})
    .meta({ id: 'DailyPerson' });
export const TeamDailyResponse = z
    .object({
    date: IsoDate,
    /** The working day after `date` (holiday calendar), the day `due_next_day` counts. */
    next_day: IsoDate,
    totals: z.object({
        done: z.number().int().min(0),
        /** Dates moved (pushed). */
        moved: z.number().int().min(0),
        /** Tasks left out that day (dropped_items), in scope. */
        left_out: z.number().int().min(0),
        overdue: z.number().int().min(0),
    }),
    /** Managers' view, everyone: the people with nothing done yet that day (prototype "Nothing logged yet from …"). */
    quiet: z.array(UserRef),
    /** Managers: the associate director first, then members by name (or just `user_id`). A member: themselves. */
    people: z.array(DailyPerson),
})
    .meta({ id: 'TeamDailyResponse' });
// ---------------------------------------------------------------------------
// GET /team/created-today — managers
// ---------------------------------------------------------------------------
export const CreatedTodayRowKind = z.enum(['task', 'proposed', 'left_out']).meta({ id: 'CreatedTodayRowKind' });
export const CreatedTodayStatus = z.enum(['active', 'done', 'proposed', 'waiting', 'left_out']).meta({ id: 'CreatedTodayStatus' });
export const CreatedTodayRow = z
    .object({
    kind: CreatedTodayRowKind,
    /** task id / proposed item id / dropped item id, by kind. */
    id: Uuid,
    at: Timestamp,
    title: z.string(),
    client: ClientRef.nullable(),
    /** Where it came from. A left-out row from a task list reads `adhoc`; a pending proposal reads `email`. */
    source: TaskSource,
    /** Who it went to (a proposal: the suggested owner, else the client's SPOC). */
    assignee: UserRef.nullable(),
    due: IsoDate.nullable(),
    /** `waiting` = a proposal pending PROPOSED_LATE_HOURS (4) or more. */
    status: CreatedTodayStatus,
    /** Whole hours a proposal has waited; null for other rows. */
    waiting_hours: z.number().int().min(0).nullable(),
    left_out_reason: DropReason.nullable(),
})
    .meta({ id: 'CreatedTodayRow' });
export const CreatedTodayResponse = z
    .object({
    date: IsoDate,
    late_hours: z.number().int(),
    totals: z.object({
        /** Tasks made today (not proposals, not left out). */
        made: z.number().int().min(0),
        /** Tasks made today from emails. */
        from_email: z.number().int().min(0),
        /** Email proposals still waiting for a SPOC (any day). */
        waiting_spoc: z.number().int().min(0),
        /** …of which waiting late_hours or more. */
        waiting_too_long: z.number().int().min(0),
        left_out: z.number().int().min(0),
    }),
    /** Counts for the source chips, tasks made today plus pending proposals (as email); left-out rows excluded. */
    by_source: z.record(z.string(), z.number().int().min(0)),
    /** Newest first: tasks made today, every pending proposal, and what was left out today. */
    rows: z.array(CreatedTodayRow),
})
    .meta({ id: 'CreatedTodayResponse' });
// ---------------------------------------------------------------------------
// GET /team/record — Appraisal record (managers) / My record (a member, themselves only)
// ---------------------------------------------------------------------------
/** Prototype PERIODS: all time, last 30, 90 or 365 days. */
export const RecordPeriod = z.enum(['all', '30', '90', '365']).meta({ id: 'RecordPeriod' });
export const TeamRecordQuery = z.object({
    /** Managers: whose entries to list (default: the first person in `people`). Members: only their own id. */
    user_id: Uuid.optional(),
    period: RecordPeriod.default('all'),
    /** Filters `entries` only; the counts in `people` and `went_right` ignore it. */
    kind: IncidentKind.optional(),
    /**
     * `entries` lists the live entries (default) or, with `true`, only the dismissed ones (prototype "Show
     * dismissed"). Dismissed entries are never deleted and never counted.
     */
    dismissed: z.enum(['true', 'false']).transform((s) => s === 'true').optional(),
});
/**
 * One row of the managers' table (prototype recStats). Counts are of entries on their watch or caused by them;
 * dismissed entries are never counted.
 */
export const RecordCounts = z
    .object({
    user: UserRef,
    /** On their client or task (owner). */
    on_watch: z.number().int().min(0),
    /** A manager decided it was their mistake (cause person, caused_by them — or the owner when unset). */
    caused: z.number().int().min(0),
    escalations: z.number().int().min(0),
    missed: z.number().int().min(0),
    /** late + slip. */
    late_or_slipped: z.number().int().min(0),
    complaints: z.number().int().min(0),
    /** cause unclear: the manager's review list. */
    not_reviewed: z.number().int().min(0),
})
    .meta({ id: 'RecordCounts' });
export const RecordResponse = z
    .object({ text: z.string(), by: UserRef.nullable(), at: Timestamp })
    .meta({ id: 'RecordResponse' });
export const RecordDismissal = z
    .object({ reason: z.string(), by: UserRef.nullable(), at: Timestamp })
    .meta({ id: 'RecordDismissal' });
export const RecordTrailLine = z
    .object({ at: Timestamp, by: UserRef.nullable(), text: z.string() })
    .meta({ id: 'RecordTrailLine' });
export const RecordEntry = z
    .object({
    id: Uuid,
    kind: IncidentKind,
    title: z.string(),
    client: ClientRef.nullable(),
    occurred_on: IsoDate,
    severity: Severity,
    cause: IncidentCause,
    status: z.enum(['open', 'closed']),
    /** How it relates to the person listed: it was on their watch, or (only) caused by them. */
    relation: z.enum(['on_watch', 'caused']),
    owner: UserRef.nullable(),
    caused_by: UserRef.nullable(),
    /** history = imported from old records; live with is_auto = noticed by the system; else logged by raised_by. */
    source: z.enum(['live', 'history']),
    is_auto: z.boolean(),
    raised_by: UserRef.nullable(),
    note: z.string().nullable(),
    outcome: z.string().nullable(),
    closed_on: IsoDate.nullable(),
    /** For If-Match on the writes below (ETag carries it too). */
    version: Version,
    /** "<Owner>'s side" — what the person it is about wrote (POST /incidents/{id}/response); null = nothing yet. */
    response: RecordResponse.nullable(),
    /** Set once a manager dismissed it (with a reason). It stays in the record, never counted. */
    dismissed: RecordDismissal.nullable(),
    /** Who did what to this entry, oldest first (incident_log). Entries made before the trail existed have none. */
    trail: z.array(RecordTrailLine),
})
    .meta({ id: 'RecordEntry' });
/** The side card: facts, never a score. Over the same period as the entries. */
export const RecordWentRight = z
    .object({
    tasks_closed: z.number().int().min(0),
    client_tasks_kept: z.number().int().min(0),
    escalations_resolved: z.number().int().min(0),
    ideas_live: z.number().int().min(0),
    /** Imported (history) tasks closed with a known close date, and how many of those on or before their due. */
    history_closed: z.number().int().min(0),
    history_closed_on_time: z.number().int().min(0),
})
    .meta({ id: 'RecordWentRight' });
export const TeamRecordResponse = z
    .object({
    period: RecordPeriod,
    /** First day counted; null for all time. */
    since: IsoDate.nullable(),
    /** Managers: every ops person (associate director first). A member: just themselves. */
    people: z.array(RecordCounts),
    /** Whose entries follow. */
    person: UserRef,
    /** Newest first. */
    entries: z.array(RecordEntry),
    went_right: RecordWentRight,
    /** Not reviewed across everyone in `people` (the sidebar badge for managers). */
    not_reviewed_total: z.number().int().min(0),
})
    .meta({ id: 'TeamRecordResponse' });
// ---------------------------------------------------------------------------
// Appraisal-record writes (prototype incModal / attrModal / disModal / respModal). Each returns the RecordEntry as
// it reads in its owner's record (relation `on_watch`; `caused` when the entry has no owner).
// ---------------------------------------------------------------------------
export const IncidentIdParams = z.object({ id: Uuid });
const NoteText = z.string().trim().max(2000);
/**
 * POST /incidents — "+ Log an incident": anything the system could not notice, or something from before it was
 * used. `owner_id` (on whose watch) must be an active ops person. `cause` person blames `caused_by_id`, else the
 * owner, and then needs `note` (what went wrong, so the person can see it). An `outcome` records it as closed.
 * It is written to the appraisal record only: an escalation kind does not open an escalation.
 */
export const CreateIncidentBody = z
    .object({
    kind: IncidentKind,
    title: z.string().trim().min(3, 'Say what happened').max(200),
    /** Omit or null = internal. Must be a client that exists (422 otherwise). */
    client_id: Uuid.nullable().optional(),
    owner_id: Uuid,
    /** Today or earlier (IST). */
    occurred_on: IsoDate,
    severity: Severity.default('med'),
    cause: IncidentCause.default('unclear'),
    caused_by_id: Uuid.nullable().optional(),
    note: NoteText.optional(),
    /** Blank = still open. */
    outcome: NoteText.optional(),
})
    .strict()
    .meta({ id: 'CreateIncidentBody' });
/**
 * PATCH /incidents/{id}/cause — "Review and decide the cause" / "Change the cause". `cause` cannot go back to
 * unclear. person: `caused_by_id` (an active ops person; default the owner) and a note (this one, or the one
 * already there) are needed. Omitted fields keep their value; status is not changed.
 */
export const DecideIncidentCauseBody = z
    .object({
    cause: IncidentCause.exclude(['unclear']),
    caused_by_id: Uuid.nullable().optional(),
    severity: Severity.optional(),
    note: NoteText.optional(),
    outcome: NoteText.optional(),
})
    .strict()
    .meta({ id: 'DecideIncidentCauseBody' });
/** POST /incidents/{id}/dismiss — never deleted: kept, marked dismissed, with this reason. */
export const DismissIncidentBody = z
    .object({ reason: z.string().trim().min(1, 'A reason is needed').max(500) })
    .strict()
    .meta({ id: 'DismissIncidentBody' });
/** POST /incidents/{id}/response — "Add my side" / "Edit my side": replaces the earlier text. */
export const IncidentResponseBody = z
    .object({ text: z.string().trim().min(1, 'Write a line first').max(2000) })
    .strict()
    .meta({ id: 'IncidentResponseBody' });
// ---------------------------------------------------------------------------
// GET /team/workload — managers
// ---------------------------------------------------------------------------
/** ADR §8.9 `workload.tasks_per_full_load`: open tasks that make a full load. A constant until Settings carries it. */
export const WORKLOAD_TASKS_PER_FULL_LOAD = 8;
export const WorkloadPerson = z
    .object({
    user: UserRef,
    open: z.number().int().min(0),
    overdue: z.number().int().min(0),
    due_today: z.number().int().min(0),
    /** Tasks they closed Monday–today (IST). */
    done_this_week: z.number().int().min(0),
    /** Minutes in today's day plan, as hours (null when there is no plan for today). Against 7.5. */
    hours_planned: z.number().nullable(),
    /** Due dates they pushed in the last 30 days. */
    slips_30d: z.number().int().min(0),
    /** The user's own capacity adjustment (percentage points), already in load_pct. */
    capacity_adjustment_pct: z.number().int(),
    /** min(100, max(0, round(open / tasks_per_full_load × 100) + capacity_adjustment_pct)). */
    load_pct: z.number().int().min(0).max(100),
    /** red > 85, amber > 60, else green (the meter colour). */
    load: z.enum(['green', 'amber', 'red']),
})
    .meta({ id: 'WorkloadPerson' });
export const TeamWorkloadResponse = z
    .object({
    as_of: IsoDate,
    tasks_per_full_load: z.number().int().min(1),
    totals: z.object({
        open: z.number().int().min(0),
        overdue: z.number().int().min(0),
        due_today: z.number().int().min(0),
        done_this_week: z.number().int().min(0),
    }),
    /** The associate director first, then members by name. */
    people: z.array(WorkloadPerson),
})
    .meta({ id: 'TeamWorkloadResponse' });
//# sourceMappingURL=team.js.map